import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import EmptyState from "@/components/base/EmptyState";
import { specials } from "@/mocks/specials";
import { getSpecialById, getTitleById } from "@/utils/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function SpecialDetail() {
  const { id } = useParams<{ id: string }>();
  const special = id ? getSpecialById(id) : undefined;

  useDocumentMeta(
    special ? `${special.title} - 午夜影院专题` : "专题不存在 - 午夜影院",
    special?.intro.slice(0, 80)
  );

  if (!special) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-bookmark-3-line"
          title="专题不存在或已下架"
          description="这个专题可能已经结束。你可以回到专题集合页，看看其他正在进行的策划。"
          actionLabel="返回专题集合"
          actionTo="/special"
        />
      </div>
    );
  }

  const titles = special.titleIds
    .map((titleId) => getTitleById(titleId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  const related = specials.filter((item) => item.id !== special.id).slice(0, 3);

  return (
    <div className="w-full bg-background-50">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={special.cover}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/40" />
        </div>

        <div className="relative mx-auto w-full max-w-[1280px] px-4 py-9 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-background-400">
            <Link to="/" className="transition-colors duration-300 hover:text-secondary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/special" className="transition-colors duration-300 hover:text-secondary-300">
              专题策划
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-200">{special.title}</span>
          </nav>

          <p className="mt-6 inline-flex rounded-sm bg-secondary-500 px-2 py-1 font-label text-[10px] font-medium text-primary-950">
            {special.month}
          </p>
          <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-background-50 md:text-[40px]">
            {special.title}
          </h1>
          <p className="mt-2 font-label text-sm tracking-wide text-secondary-300">{special.subtitle}</p>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-background-200/90">{special.intro}</p>
          <p className="mt-5 font-label text-xs text-background-400">
            收录 {titles.length} 部作品 · 编辑推荐
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_280px] lg:gap-12">
          <div>
            <SectionHeading
              kicker="本期片单"
              title="专题推荐作品"
              description="点击任意卡片进入影视详情页，查看完整资料与相关推荐。"
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {titles.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>

            <section className="mt-10 rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-secondary-950">
                <i className="ri-double-quotes-l text-secondary-700" />
                编辑推荐理由
              </h2>
              <div className="mt-3 space-y-3">
                {special.reasons.map((reason, index) => (
                  <p key={index} className="text-sm leading-7 text-secondary-900/85">
                    {reason}
                  </p>
                ))}
              </div>
              <p className="mt-4 border-t border-secondary-200 pt-3 text-[11px] text-secondary-800/70">
                以上推荐基于站内示例数据整理，内容仅代表编辑部视角。
              </p>
            </section>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-4">
              <h2 className="font-heading text-sm font-semibold text-foreground-950">专题信息</h2>
              <dl className="mt-3 space-y-3 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">期数</dt>
                  <dd className="text-foreground-800">{special.month}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">收录作品</dt>
                  <dd className="text-foreground-800">{titles.length} 部</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">策展</dt>
                  <dd className="text-foreground-800">午夜影院编辑部</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="font-heading text-sm font-semibold text-foreground-950">相关专题</h2>
              <ul className="mt-3 space-y-2.5">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/special/${item.id}`}
                      className="group flex items-start justify-between gap-2 text-xs leading-relaxed text-foreground-700 transition-colors duration-300 hover:text-accent-700"
                    >
                      <span>
                        {item.title}
                        <span className="mt-0.5 block text-[11px] text-foreground-400">
                          {item.subtitle}
                        </span>
                      </span>
                      <i className="ri-arrow-right-up-line mt-0.5 shrink-0 text-foreground-300" />
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/special"
                className="mt-4 flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors duration-300 hover:border-accent-400 hover:text-accent-700"
              >
                全部专题
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}